import React, { useEffect } from "react";
import { DataStyling } from "../data-styling";
import DataActions from "./DataActions";
import { SiteMapSurfaceType, SurfaceType } from "../DataAndMap";
import {
  SimpleFeatureVisibility,
  SimpleLatLng,
  SiteMapSurfaceFeatureType,
} from "../Core";
import { useMapDataLayer } from "../contexts/DataLayerContext";

/**
 * In building the ability to add polygons with holes, we've semi-regularly
 * run into issues where lat/lngs with 9 digit specificity (e.g. 37.123456789)
 * that are identical in value cause Google Maps to properly draw a polygon
 * as a hole, but NOT to cut it out of the parent container.
 *
 * If we drop specificity down to 5 digits (1 meter resolution), then
 * holes get cut out again, but we can't afford to lose that precision across
 * the board.
 *
 * We also find reliably that if we remove a last point from each polygon
 * ring that Google Maps reliably draws the polygons with holes as desired
 */
const stripEndpointFromRing = (
  latLngRing: google.maps.LatLng[],
): google.maps.LatLng[] => {
  if (latLngRing.length > 1) {
    const first = latLngRing[0];
    const last = latLngRing[latLngRing.length - 1];

    // make sure we don't hit floating point precision issues as we check this ourselves
    if (
      Math.abs(first.lat() - last.lat()) < 0.0000001 &&
      Math.abs(first.lng() - last.lng()) < 0.0000001
    ) {
      return latLngRing.slice(0, -1); // Return array without the duplicate last point
    }
  }
  return latLngRing;
};
const stripSimpleEndpointFromRing = (
  latLngRing: SimpleLatLng[],
): SimpleLatLng[] => {
  if (latLngRing.length > 1) {
    const first = latLngRing[0];
    const last = latLngRing[latLngRing.length - 1];

    // make sure we don't hit floating point precision issues as we check this ourselves
    if (
      Math.abs(first.lat - last.lat) < 0.0000001 &&
      Math.abs(first.lng - last.lng) < 0.0000001
    ) {
      return latLngRing.slice(0, -1); // Return array without the duplicate last point
    }
  }
  return latLngRing;
};

// Map controls sidebar component
export default function SurfaceFeatures({
  editing,
  features,
  surfaceTypes,
  featureVisibility,
  selectedSurfaceId,
  onFeatureClick,
  deleteSurfaceFeature,
  updateSurfaceFeature,
}: {
  editing: boolean;
  features: SiteMapSurfaceType[];
  surfaceTypes: SurfaceType[];
  featureVisibility: SimpleFeatureVisibility;
  selectedSurfaceId: string | undefined;
  onFeatureClick: (event: google.maps.Data.MouseEvent) => void;
  deleteSurfaceFeature: (feature: SiteMapSurfaceType) => void;
  updateSurfaceFeature: (feature: SiteMapSurfaceType) => void;
}) {
  const { surfaceData } = useMapDataLayer();

  const onFeatureRightClick = (event: google.maps.Data.MouseEvent) => {
    const clickedPoint = event.latLng;
    const feature = event.feature;
    if (!feature || !clickedPoint) return;

    const geometry = feature.getGeometry() as
      | google.maps.Data.LineString
      | google.maps.Data.Polygon;
    if (!geometry) return;

    const featureId = feature.getProperty("id") as number;

    if (geometry instanceof google.maps.Data.LineString) {
      const points = geometry.getArray();
      // filter the clicked point from our points array
      const newPoints = points.filter(
        (point: google.maps.LatLng) =>
          point.lat().toPrecision(7) !== clickedPoint.lat().toPrecision(7) ||
          point.lng().toPrecision(8) !== clickedPoint.lng().toPrecision(8),
      );

      if (newPoints.length < 2) {
        deleteSurfaceFeature({
          id: featureId,
          type: "Polygon",
          coordinates: [
            points.map((point: google.maps.LatLng) => ({
              lat: point.lat(),
              lng: point.lng(),
            })),
          ],
          properties: {
            surfaceType: "parkingLot",
          },
        });
        return;
      }

      if (newPoints.length !== points.length) {
        feature.setGeometry(new google.maps.Data.LineString(newPoints));
      }
    } else {
      // Handle polygon - check all rings for the clicked point
      const rings = geometry.getArray();
      const newRings: google.maps.LatLng[][] = [];
      let pointFoundAndRemoved = false;
      let outerRingRemoved = false;

      // Process each ring (outer ring and holes)
      let i = 0;
      rings.forEach((ring) => {
        const ringPoints: google.maps.LatLng[] = [];

        ring.forEachLatLng((latLng: google.maps.LatLng) => {
          ringPoints.push(latLng);
        });

        // Filter out the clicked point from this ring
        const filteredPoints = ringPoints.filter(
          (point: google.maps.LatLng) =>
            point.lat().toPrecision(7) !== clickedPoint.lat().toPrecision(7) ||
            point.lng().toPrecision(8) !== clickedPoint.lng().toPrecision(8),
        );

        // Check if a point was removed from this ring
        if (filteredPoints.length !== ringPoints.length) {
          pointFoundAndRemoved = true;
        }

        // For outer ring (index 0), need at least 3 points
        // For holes (index > 0), need at least 3 points to remain valid
        const minPoints = 3;

        if (filteredPoints.length >= minPoints) {
          newRings.push(filteredPoints);
        }
        if (i === 0 && filteredPoints.length < minPoints) {
          outerRingRemoved = true;
        }
        // If ring has fewer than minimum points, it gets removed (not added to newRings)

        i++;
      });

      // If outer ring was removed or doesn't exist, delete the entire feature
      if (outerRingRemoved) {
        deleteSurfaceFeature({
          id: featureId,
          type: "Polygon",
          coordinates: [],
          properties: {
            surfaceType: "parkingLot",
          },
        });
        return;
      }

      // Update geometry if a point was found and removed
      if (pointFoundAndRemoved) {
        const strippedRings = newRings.map((ring) =>
          stripEndpointFromRing(ring),
        );
        feature.setGeometry(new google.maps.Data.Polygon(strippedRings));
      }
    }
  };

  const onFeatureSetGeometry = (event: google.maps.Data.SetGeometryEvent) => {
    // Extract the updated feature and its geometry from the event
    const feature = event.feature;
    const geometry = feature.getGeometry();

    if (!feature || !geometry) return;

    // Get feature properties to reconstruct the SiteMapFeature
    const featureId = feature.getProperty("id") as number;
    const surfaceType = feature.getProperty(
      "surfaceType",
    ) as SiteMapSurfaceFeatureType;

    // Convert Google Maps Data geometry back to our SiteMapFeature format
    if (geometry.getType() === "Polygon") {
      const polygon = geometry as google.maps.Data.Polygon;

      // Extract coordinates from the polygon rings properly
      const coordinates: SimpleLatLng[][] = [];

      // Get the array of rings from the polygon
      const rings = polygon.getArray();

      for (let i = 0; i < rings.length; i++) {
        const ring = rings[i];
        const ringCoordinates: SimpleLatLng[] = [];

        // Get coordinates from each ring
        ring.forEachLatLng((latLng: google.maps.LatLng) => {
          ringCoordinates.push({
            lat: latLng.lat(),
            lng: latLng.lng(),
          });
        });

        coordinates.push(stripSimpleEndpointFromRing(ringCoordinates));
      }

      // If no coordinates were collected, initialize with a single ring
      if (coordinates.length === 0) {
        coordinates.push([]);
      }

      // Create the updated SiteMapFeature
      const updatedFeature: SiteMapSurfaceType = {
        id: featureId,
        type: "Polygon",
        coordinates,
        properties: {
          surfaceType: surfaceType || "parkingLot",
        },
      };

      // Call the update function with the converted feature
      updateSurfaceFeature(updatedFeature);
    }
  };

  return (
    <>
      {surfaceData ? (
        <>
          <DataActions
            surfaceData={surfaceData}
            onFeatureClick={onFeatureClick}
            onFeatureRightClick={onFeatureRightClick}
            onFeatureSetGeometry={onFeatureSetGeometry}
          />
          <DataStyling
            surfaceTypes={surfaceTypes}
            surfaceData={surfaceData}
            featureVisibility={featureVisibility}
          />
          {features.map((feature) => (
            <MapFeature
              surfaceData={surfaceData}
              key={feature.id}
              feature={feature}
              editing={editing}
              selected={String(feature.id) === String(selectedSurfaceId)}
            />
          ))}
        </>
      ) : null}
    </>
  );
}

const MapFeature = ({
  feature,
  editing,
  selected,
  surfaceData,
}: {
  feature: SiteMapSurfaceType;
  editing: boolean;
  selected: boolean;
  surfaceData: google.maps.Data | null;
}) => {
  // Create a serialized version of coordinates for dependency tracking
  const coordsString = JSON.stringify(feature.coordinates);

  // Track the current data feature for cleanup
  const currentDataFeature = React.useRef<google.maps.Data.Feature | null>(
    null,
  );

  useEffect(() => {
    if (!surfaceData) return;

    // Check if feature already exists - try both methods
    const existingFeature = surfaceData.getFeatureById(feature.id);

    if (existingFeature) {
      // Check if geometry actually needs updating by comparing ring counts
      const currentGeometry =
        existingFeature.getGeometry() as google.maps.Data.Polygon;
      // const currentRingCount = currentGeometry
      //   ? currentGeometry.getArray().length
      //   : 0;

      // Only update geometry if ring count changed
      if (!isSamePolygon(currentGeometry, feature.coordinates)) {
        // Convert coordinates to Google Maps LatLng objects
        const rings = feature.coordinates.map((ring) => {
          const latLngRing = ring.map(
            (coord) => new google.maps.LatLng(coord.lat, coord.lng),
          );

          return stripEndpointFromRing(latLngRing);
        });

        // Update the existing feature's geometry
        existingFeature.setGeometry(new google.maps.Data.Polygon(rings));
      }

      // Always update properties (these are less likely to cause loops)
      existingFeature.setProperty("editing", editing);
      existingFeature.setProperty("selected", selected);
      existingFeature.setProperty(
        "surfaceType",
        feature.properties.surfaceType || "parkingLot",
      );

      // Update our reference to this feature
      currentDataFeature.current = existingFeature;

      return;
    }

    // Convert coordinates to Google Maps LatLng objects for new features
    const rings = feature.coordinates.map((ring) => {
      const latLngRing = ring.map(
        (coord) => new google.maps.LatLng(coord.lat, coord.lng),
      );

      return stripEndpointFromRing(latLngRing);
    });

    // Add new feature to data layer
    const dataFeature = surfaceData.add({
      id: feature.id, // Set the actual feature ID
      geometry: new google.maps.Data.Polygon(rings),
      properties: {
        id: feature.id, // Keep as property too for backwards compatibility
        editing,
        selected,
        surfaceType: feature.properties.surfaceType || "parkingLot",
      },
    });

    // Store reference to the data feature
    currentDataFeature.current = dataFeature;
  }, [
    surfaceData,
    editing,
    feature.id,
    feature.coordinates,
    coordsString,
    feature.properties.surfaceType,
    selected,
  ]);

  // Update selected state when it changes
  useEffect(() => {
    if (!surfaceData) return;

    const existingFeature = surfaceData.getFeatureById(feature.id);
    if (existingFeature) {
      existingFeature.setProperty("editing", editing);
      existingFeature.setProperty("selected", selected);
    }
  }, [surfaceData, feature.id, editing, selected]);

  // Cleanup only when component unmounts (not on coordinate changes)
  useEffect(() => {
    const featureId = feature.id;
    const dataLayer = surfaceData;

    return () => {
      if (currentDataFeature.current && dataLayer) {
        // console.log("Cleaning up feature on unmount:", featureId);
        try {
          dataLayer.remove(currentDataFeature.current);
          // currentDataFeature.current = null;
        } catch {
          console.log("Feature already removed on unmount:", featureId);
        }
      }
    };
  }, [feature.id, surfaceData]);

  return null;
};
function isSamePolygon(
  googlePolygon: google.maps.Data.Polygon,
  other: SimpleLatLng[][],
): boolean {
  const googleArray = googlePolygon.getArray();
  if (googleArray.length !== other.length) {
    return false;
  }
  for (let i = 0; i < googleArray.length; i++) {
    const googlePolyPoints = googleArray[i].getArray();
    const otherPoints = stripSimpleEndpointFromRing(other[i]);
    if (googlePolyPoints.length !== otherPoints.length) {
      return false;
    }
    for (let j = 0; j < googlePolyPoints.length; j++) {
      const googlePoint = googlePolyPoints[j];
      const otherPoint = otherPoints[j];
      if (Math.abs(googlePoint.lat() - otherPoint.lat) > 0.0000000001) {
        return false;
      }
      if (Math.abs(googlePoint.lng() - otherPoint.lng) > 0.0000000001) {
        return false;
      }
    }
  }
  return true;
}
